/**
 * Collection 页筛选：默认附带「In stock」(filter.v.availability=1)
 *
 * 目标
 * - 用户首次点击任意其它 filter 时，自动在请求 URL 中追加 filter.v.availability=1（等同默认勾选 In stock）
 * - 首次进入 Collection 且 URL 无任何 filter 时，不注入，仍显示全部商品
 * - 用户可手动取消 In stock，或通过 active facet 药丸移除；之后不再自动注入
 * - 用户可手动勾选 Out of stock（filter.v.availability=0）；已有 availability 参数时不干预
 * - Clear All 会清除 opt-out，恢复「未筛选」初始行为
 *
 * 范围：仅 Collection（data-section-type="collection-template"），不含 Search
 *
 * Out of stock 参数：filter.v.availability=0
 *
 * 注入入口：onSubmitForm、onActiveFilterClick、popstate（浏览器前进/后退）
 *
 * Opt-out：sessionStorage，key 为 collectionFacetAvailabilityOptOut 或 collectionFacetAvailabilityOptOut:{handle}
 * collectionFacetAvailabilityOptOut = 1 的含义： 不要 再自动加 In stock（用户已 opt-out）
 *
 * 说明：若 URL/表单已含 filter.v.availability（例如分享链接带 =1），applyDefaultInStock 不会重复 append，
 *       由服务端渲染的勾选状态经 FormData 继续提交即可。
 */
const AVAILABILITY_PARAM = 'filter.v.availability';
const AVAILABILITY_IN_STOCK_VALUE = '1';
const AVAILABILITY_OPT_OUT_KEY = 'collectionFacetAvailabilityOptOut';

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  /** 是否为 Collection 商品列表页（main-collection-product-grid） */
  static isCollectionPage() {
    return Boolean(document.querySelector('[data-section-type="collection-template"]'));
  }

  /** 按 collection handle 区分 opt-out，避免不同集合页互相影响 */
  static getAvailabilityOptOutKey() {
    const collectionEl = document.querySelector('[data-section-type="collection-template"]');
    const handle = collectionEl?.dataset?.collection;
    return handle ? `${AVAILABILITY_OPT_OUT_KEY}:${handle}` : AVAILABILITY_OPT_OUT_KEY;
  }

  /** 记录/清除用户主动取消「默认 In stock」的意图 */
  static setAvailabilityOptOut(optOut) {
    const key = FacetFiltersForm.getAvailabilityOptOutKey();
    if (optOut) {
      sessionStorage.setItem(key, '1');
    } else {
      sessionStorage.removeItem(key);
    }
  }

  static hasAvailabilityOptOut() {
    return sessionStorage.getItem(FacetFiltersForm.getAvailabilityOptOutKey()) === '1';
  }

  /** 是否已有任意商品筛选参数（不含仅 sort_by） */
  static hasAnyProductFilter(params) {
    return [...params.keys()].some((key) => key.startsWith('filter.'));
  }

  /**
   * 在提交前为 Collection 请求追加 filter.v.availability=1（若满足条件）
   * 不注入：非 Collection、已 opt-out、已有 availability 参数、尚无其它 filter.*
   */
  static applyDefaultInStock(searchParams) {
    if (!FacetFiltersForm.isCollectionPage() || !searchParams) return searchParams;

    const params = new URLSearchParams(searchParams);

    if (FacetFiltersForm.hasAvailabilityOptOut()) return searchParams;
    if (params.getAll(AVAILABILITY_PARAM).length > 0) return searchParams;
    if (!FacetFiltersForm.hasAnyProductFilter(params)) return searchParams;

    params.append(AVAILABILITY_PARAM, AVAILABILITY_IN_STOCK_VALUE);
    return params.toString();
  }

  /** 点击 active facet 移除 In stock 时，将此次操作视为 opt-out */
  static detectInStockRemoval(prevSearchParams, nextSearchParams) {
    const prevParams = new URLSearchParams(prevSearchParams || '');
    const nextParams = new URLSearchParams(nextSearchParams || '');
    const hadInStock = prevParams.getAll(AVAILABILITY_PARAM).includes(AVAILABILITY_IN_STOCK_VALUE);
    const hasInStock = nextParams.getAll(AVAILABILITY_PARAM).includes(AVAILABILITY_IN_STOCK_VALUE);

    if (hadInStock && !hasInStock) {
      FacetFiltersForm.setAvailabilityOptOut(true);
    }
  }

  /**
   * 绑定 opt-out 相关事件（仅 Collection 页执行一次）
   * - 取消勾选 In stock → opt-out；重新勾选 → 清除 opt-out
   * - Clear All → 清除 opt-out
   * 对应 facets.liquid 中 data-facet-availability-in-stock
   */
  static setAvailabilityListeners() {
    if (!FacetFiltersForm.isCollectionPage()) return;

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (
        !input.matches(
          '[data-facet-availability-in-stock], input[name="filter.v.availability"][value="1"]'
        )
      ) {
        return;
      }

      FacetFiltersForm.setAvailabilityOptOut(!input.checked);
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!FacetFiltersForm.isCollectionPage()) return;

        const clearAllLink = event.target.closest('a.active-facets__button-remove');
        if (clearAllLink) {
          FacetFiltersForm.setAvailabilityOptOut(false);
        }
      },
      true
    );
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const rawSearchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (rawSearchParams === FacetFiltersForm.searchParamsPrev) return;
      // 前进/后退时保持与其它筛选提交一致的默认 In stock 逻辑
      const searchParams = FacetFiltersForm.applyDefaultInStock(rawSearchParams);
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    // 勾选/取消任意 filter、改 sort 等表单提交路径
    const processedSearchParams = FacetFiltersForm.applyDefaultInStock(searchParams);
    FacetFiltersForm.renderPage(processedSearchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    if (FacetFiltersForm.isCollectionPage()) {
      const isClearAll = event.currentTarget.matches('a.active-facets__button-remove');

      if (isClearAll) {
        // Clear All：只重置 opt-out，勿走 detectInStockRemoval（否则会因移除 availability=1 误设为 opt-out）
        FacetFiltersForm.setAvailabilityOptOut(false);
      } else {
        FacetFiltersForm.detectInStockRemoval(FacetFiltersForm.searchParamsPrev, url);
      }
    }

    // 点击 active facet 药丸移除单个筛选时
    const processedSearchParams = FacetFiltersForm.applyDefaultInStock(url);
    FacetFiltersForm.renderPage(processedSearchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
// Collection 默认 In stock：注册 opt-out 监听（见文件顶部说明）
FacetFiltersForm.setAvailabilityListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
